import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import type { VoxelDocument } from '../stores/editor'

type Built = { positions: ArrayBuffer; colors: ArrayBuffer; keys: string[] }
export type Pick = { x: number; y: number; z: number; key: string; normal: THREE.Vector3 }

export class VoxelRenderer {
  private readonly scene = new THREE.Scene()
  private readonly camera = new THREE.PerspectiveCamera(42, 1, 0.1, 1000)
  private readonly renderer: THREE.WebGLRenderer
  private readonly controls: OrbitControls
  private readonly raycaster = new THREE.Raycaster()
  private readonly pointer = new THREE.Vector2()
  private readonly root = new THREE.Group()
  private mesh: THREE.InstancedMesh | null = null
  private keys: string[] = []
  private worker = new Worker(new URL('../workers/voxel.worker.ts', import.meta.url), { type: 'module' })
  onPick?: (pick: Pick) => void

  constructor(private readonly host: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); host.append(this.renderer.domElement)
    this.scene.add(this.root)
    this.camera.position.set(30, 26, 34)
    this.controls = new OrbitControls(this.camera, this.renderer.domElement); this.controls.enableDamping = true; this.controls.target.set(0, 0, 0)
    this.scene.add(new THREE.HemisphereLight(0xf2f7ff, 0x1a202d, 2.0))
    const key = new THREE.DirectionalLight(0xffffff, 3.2); key.position.set(20, 36, 18); key.castShadow = true; this.scene.add(key)
    const grid = new THREE.GridHelper(44, 44, 0x344158, 0x202938); grid.position.y = -12.5; this.scene.add(grid)
    new ResizeObserver(() => this.resize()).observe(host); this.resize(); this.draw()
    this.renderer.domElement.addEventListener('pointerdown', (event) => this.pick(event))
  }

  update(document: VoxelDocument) {
    this.worker.postMessage(structuredClone(document))
    this.worker.onmessage = ({ data }: MessageEvent<Built>) => this.apply(data)
  }

  private apply(built: Built) {
    if (this.mesh) { this.root.remove(this.mesh); this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose() }
    const positions = new Float32Array(built.positions), colors = new Float32Array(built.colors)
    this.keys = built.keys
    const geometry = new THREE.BoxGeometry(.96, .96, .96)
    const material = new THREE.MeshStandardMaterial({ roughness: .62, metalness: .08, vertexColors: true })
    this.mesh = new THREE.InstancedMesh(geometry, material, built.keys.length)
    const matrix = new THREE.Matrix4(); const colour = new THREE.Color()
    for (let index = 0; index < built.keys.length; index += 1) {
      matrix.setPosition(positions[index * 3], positions[index * 3 + 1], positions[index * 3 + 2]); this.mesh.setMatrixAt(index, matrix)
      colour.setRGB(colors[index * 3], colors[index * 3 + 1], colors[index * 3 + 2]); this.mesh.setColorAt(index, colour)
    }
    this.mesh.instanceMatrix.needsUpdate = true; if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true
    this.root.add(this.mesh)
  }

  private pick(event: PointerEvent) {
    if (!this.mesh || event.button !== 0 || event.movementX || event.movementY) return
    const rect = this.renderer.domElement.getBoundingClientRect(); this.pointer.set(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1)
    this.raycaster.setFromCamera(this.pointer, this.camera); const hit = this.raycaster.intersectObject(this.mesh)[0]
    if (!hit || hit.instanceId === undefined) return
    const [x, y, z] = this.keys[hit.instanceId].split(':').map(Number)
    this.onPick?.({ x, y, z, key: this.keys[hit.instanceId], normal: hit.face?.normal.clone() || new THREE.Vector3() })
  }

  private resize() { const { width, height } = this.host.getBoundingClientRect(); if (!width || !height) return; this.camera.aspect = width / height; this.camera.updateProjectionMatrix(); this.renderer.setSize(width, height, false) }
  private draw = () => { this.controls.update(); this.renderer.render(this.scene, this.camera); requestAnimationFrame(this.draw) }
  dispose() { this.worker.terminate(); this.controls.dispose(); this.renderer.dispose(); this.renderer.domElement.remove() }
}

